const React = require('react');
const T = require('prop-types');
const { Switch, Route, Redirect } = require('react-router-dom');
const LoadingScreen = require('components/LoadingScreen');
const GenericError = require('components/GenericError');
const { reloadPage } = require('utils/reload');
// const PageTitleByPath = require('utils/pageTitleByPath');

const {
    cloneElement,
    useEffect,
    useState
} = React;

const DEFAULT_DEPENDENCY_TIMEOUT_MS = 15000;

const internals = {};
internals.lifecycleRedirects = new WeakMap();

exports.Routes = function Routes(props) {

    let { routes } = props;
    const { onUpdate } = props;

    const {
        keyForRoute,
        renderRoute,
        cloneWithKeys,
        markBasePaths
    } = internals;

    routes = markBasePaths('/', [].concat(routes));

    const renderBase = renderRoute(routes, '/', onUpdate);

    return (
        <Switch>
            {cloneWithKeys({
                items: routes.map(renderBase),
                keyGetter: (route) => keyForRoute(route)
            })}
        </Switch>
    );
};

exports.withPropTypeDependencies = function withPropTypeDependencies(Component, options = {}) {

    const propTypes = options.propTypes || internals.getComponentDependencyPropTypes(Component);
    const dependencyPropNames = options.dependencyPropNames || internals.getDependencyPropNames(propTypes);
    const Fallback = options.fallback || internals.RouteDependencyFallback;
    const ErrorFallback = options.errorFallback || internals.RouteDependencyError;
    const timeoutMs = options.timeoutMs || DEFAULT_DEPENDENCY_TIMEOUT_MS;
    const componentName = `withPropTypeDependencies(${Component.displayName || Component.name || 'Component'})`;

    function WithPropTypeDependencies(props) {

        const [missingState, setMissingState] = useState({
            key: null,
            timedOut: false
        });

        const missingDependencies = internals.getMissingDependencies({
            componentName,
            dependencyPropNames,
            propTypes,
            props
        });

        const missingKey = missingDependencies.join('|');

        useEffect(() => {

            if (!missingKey) {
                setMissingState({
                    key: null,
                    timedOut: false
                });
                return undefined;
            }

            setMissingState({
                key: missingKey,
                timedOut: false
            });

            const timeout = setTimeout(() => {

                setMissingState((current) => {

                    if (current.key !== missingKey) {
                        return current;
                    }

                    return {
                        key: missingKey,
                        timedOut: true
                    };
                });
            }, timeoutMs);

            return () => clearTimeout(timeout);
        }, [missingKey]);

        if (missingDependencies.length) {
            if (missingState.key === missingKey && missingState.timedOut) {
                return <ErrorFallback {...props} missingDependencies={missingDependencies} componentName={componentName} />;
            }

            return <Fallback {...props} missingDependencies={missingDependencies} componentName={componentName} />;
        }

        return <Component {...props} />;
    }

    WithPropTypeDependencies.displayName = componentName;
    WithPropTypeDependencies.propTypes = Component.propTypes;
    WithPropTypeDependencies.dependencyPropTypes = propTypes;
    WithPropTypeDependencies.dependencyPropNames = dependencyPropNames;

    return WithPropTypeDependencies;
};

// Recursive prop-types strategy grabbed from
// https://github.com/facebook/react/issues/5676#issuecomment-739092902
// eslint-disable-next-line hapi/hapi-scope-start, prefer-spread
const routeType = (...args) => T.shape({
    path: T.string,
    exact: T.bool,
    strict: T.bool,
    sensitive: T.bool,
    component: T.any,
    fallback: T.any,
    dependencyErrorFallback: T.any,
    dependencyTimeoutMs: T.number,
    dependencyPropTypes: T.object,
    dependencyPropNames: T.arrayOf(T.string),
    resolveDependencies: T.oneOfType([
        T.func,
        T.arrayOf(T.func),
        T.object
    ]),
    mountWhileResolving: T.bool,
    isReadyToMount: T.func,
    wrapFallbackWithComponent: T.bool,
    redirect: T.shape({
        from: T.string,
        to: T.string
    }),
    childRoutes: T.arrayOf(routeType)
}).apply(null, args);

exports.Routes.propTypes = {
    routes: T.oneOfType([
        routeType,
        T.arrayOf(routeType)
    ]).isRequired,
    onUpdate: T.func
};

internals.markBasePaths = (basePath, routes) => {

    if (!routes) {
        return null;
    }

    const { concatPaths } = internals;

    return routes.map((route) => {

        route.basePath = basePath;

        if (route.childRoutes) {
            route.childRoutes = internals.markBasePaths(
                concatPaths(basePath, route.path),
                route.childRoutes
            );
        }

        return route;
    });
};

internals.cloneWithKeys = ({ items, keyGetter }) => {

    return items.map((item) => {

        if (!item) {
            return null;
        }

        return cloneElement(item, { key: keyGetter(item) });
    });
};

internals.keyForRoute = (route) => {

    if (!route) {
        return null;
    }

    return String(
        route.path
        + String(route.props)
        + !!route.exact
        + !!route.strict
        + !!route.sensitive
        + !!route.childRoutes
    );
};

internals.handleRedirect = function HandleRedirect(basePath, route) {

    const { concatPaths } = internals;

    // Redirect is special and must be exclusive of other props
    let { redirect: { from, to } } = route;

    if (typeof from === 'string') {
        // redirect.from must be relative
        from = concatPaths(basePath, from);
    }

    if (typeof to === 'string') {
        // If redirect.to is absolute, leave it be. Otherwise make it relative
        to = to.startsWith('/') ? to : concatPaths(basePath, to);
    }
    else if (to && typeof to.pathname === 'string') {
        // to is an object
        to = {
            ...to,
            pathname: to.pathname.startsWith('/') ? to.pathname : concatPaths(basePath, to.pathname)
        };
    }

    return <Redirect from={from} to={to} />;
};

internals.renderRoute = function RenderPath(routes, basePath, onUpdate) {

    return function RenderRoute(route) {

        const {
            handleRedirect,
            concatPaths,
            renderRoute,
            keyForRoute,
            cloneWithKeys,
            RouteComponentLifecycleWrapper
        } = internals;

        if (!route) {
            return null;
        }

        const {
            path,
            exact,
            strict,
            sensitive,
            component: RouteComponent,
            wrapFallbackWithComponent
        } = route;

        // console.log('zlog path', path);

        const normalizedPath = concatPaths(basePath, path);
        const renderRouteFromPath = renderRoute(routes, normalizedPath, onUpdate);

        return (
            <Route
                path={normalizedPath}
                exact={exact}
                strict={strict}
                sensitive={sensitive}
                render={(props) => {

                    if (route.redirect) {
                        return handleRedirect(basePath, route);
                    }

                    if (!route || !path || !RouteComponent) {
                        return null;
                    }

                    const routerProps = {
                        ...props,
                        ...route.props,
                        route
                    };

                    const render = function Render(renderProps) {

                        const componentProps = { ...routerProps, ...renderProps };

                        if (onUpdate) {
                            onUpdate(componentProps);
                        }

                        return (
                            <RouteComponent {...componentProps}>
                                {route.childRoutes && (
                                    <Switch>
                                        {cloneWithKeys({
                                            // 'childRoutes' mapped to 'renderRouteFromPath' — It's a recursion excursion! =P
                                            items: [].concat(route.childRoutes).map(renderRouteFromPath),
                                            keyGetter: (routeEl) => normalizedPath + keyForRoute(routeEl.props)
                                        })}
                                    </Switch>
                                )}
                            </RouteComponent>
                        );
                    };

                    if (RouteComponentLifecycleWrapper.isTrivialRoute(route)) {
                        return render();
                    }

                    const renderFallback = function RenderFallback(renderProps) {

                        const { routeError } = renderProps || {};
                        const Fallback = routeError
                            ? route.dependencyErrorFallback || internals.RouteDependencyError
                            : route.fallback || route.props?.fallback || null;

                        const componentProps = { ...routerProps, ...renderProps };

                        if (Fallback) {
                            if (wrapFallbackWithComponent) {
                                return (
                                    <RouteComponent {...componentProps}>
                                        <Fallback {...componentProps} />
                                    </RouteComponent>
                                );
                            }

                            return <Fallback {...componentProps} />;
                        }

                        return null;
                    };

                    return (
                        <RouteComponentLifecycleWrapper
                            key={route.getLifecycleKey?.(routerProps)}
                            route={route}
                            routerProps={routerProps}
                            // eslint-disable-next-line react/jsx-no-bind
                            render={render}
                            // eslint-disable-next-line react/jsx-no-bind
                            renderFallback={renderFallback}
                        />
                    );
                }}
            />
        );
    };
};

internals.RouteComponentLifecycleWrapper = function RouteComponentLifecycleWrapper(props) {

    const { route, routerProps } = props;
    const shouldMountWhileResolving = internals.RouteComponentLifecycleWrapper.canMountWhileResolving(route);

    const [mountState, setMountState] = useState(() => ({
        shouldMount: shouldMountWhileResolving,
        routeError: null
    }));

    // console.log('zlog shouldMount, route', mountState.shouldMount, route);

    useEffect(() => {

        let isCurrentRoute = true;
        const shouldMountWhileResolvingOnThisRoute = internals.RouteComponentLifecycleWrapper.canMountWhileResolving(route);

        if (mountState.shouldMount !== shouldMountWhileResolvingOnThisRoute || mountState.routeError) {
            setMountState({
                shouldMount: shouldMountWhileResolvingOnThisRoute,
                routeError: null
            });
        }

        const { history } = routerProps;

        let {
            onWillMount,
            onUnmount
        } = route;

        const noop = () => null;

        onWillMount = onWillMount || noop;
        onUnmount = onUnmount || noop;

        const runRouteLifecycle = async () => {

            try {
                const willMountResult = await onWillMount(routerProps) || {};

                if (!isCurrentRoute) {
                    return;
                }

                const shouldStopAfterWillMount = internals.handleLifecycleRedirect({
                    history,
                    route,
                    result: willMountResult
                });

                if (shouldStopAfterWillMount || !isCurrentRoute) {
                    return;
                }

                internals.recordRouteDependencyDebug('route-dependencies:start', {
                    routePath: `${route.basePath || ''}${route.path || ''}`,
                    pathname: routerProps.location?.pathname,
                    dependencyPropNames: internals.getRouteDependencyContext({
                        route,
                        routerProps
                    }).dependencyPropNames
                });

                const dependencyResult = await internals.resolveRouteDependencies({
                    route,
                    routerProps
                }) || {};

                if (!isCurrentRoute) {
                    return;
                }

                internals.recordRouteDependencyDebug('route-dependencies:ready', {
                    routePath: `${route.basePath || ''}${route.path || ''}`,
                    pathname: routerProps.location?.pathname
                });

                const shouldStopAfterDependencies = internals.handleLifecycleRedirect({
                    history,
                    route,
                    result: dependencyResult
                });

                if (shouldStopAfterDependencies || !isCurrentRoute) {
                    return;
                }

                setMountState({
                    shouldMount: true,
                    routeError: null
                });
            }
            catch (err) {

                console.error(err);

                internals.recordRouteDependencyDebug('route-dependencies:error', {
                    routePath: `${route.basePath || ''}${route.path || ''}`,
                    pathname: routerProps.location?.pathname,
                    error: {
                        code: err?.code,
                        message: err?.message,
                        routePath: err?.routePath,
                        resolverName: err?.resolverName,
                        dependencyPropNames: err?.dependencyPropNames
                    }
                });

                if (!isCurrentRoute) {
                    return;
                }

                setMountState({
                    shouldMount: false,
                    routeError: err
                });
            }
        };

        // console.log('zlog route', route);
        // console.trace();

        runRouteLifecycle();

        return function cleanup() {

            isCurrentRoute = false;
            onUnmount(routerProps);
        };

    }, [routerProps.match.url]);

    const { renderFallback, render, ...rest } = props;

    const isReadyToMount = !route.isReadyToMount || route.isReadyToMount();

    return mountState.shouldMount && isReadyToMount
        ? render(rest)
        : renderFallback({
            ...rest,
            routeError: mountState.routeError
        });
};

internals.RouteComponentLifecycleWrapper.propTypes = {
    route: T.object.isRequired
};

internals.RouteComponentLifecycleWrapper.isTrivialRoute = (route) => {

    if (!route) {
        return true;
    }

    return !route.onWillMount && !route.onUnmount && !route.resolveDependencies;
};

internals.RouteComponentLifecycleWrapper.canMountWhileResolving = (route) => {

    if (!route) {
        return false;
    }

    if (route.isReadyToMount && !route.isReadyToMount()) {
        return false;
    }

    // Routes with dependency prop-types have their own render guard. Mounting them while
    // the route resolver runs lets cached Redux data render immediately, while missing
    // dependencies still show the component-level loading state until the resolver finishes.
    return !!(route.mountWhileResolving || route.dependencyPropTypes);
};

internals.handleLifecycleRedirect = ({ history, route, result }) => {

    const { replace } = result || {};

    if (!replace) {
        return false;
    }

    const from = internals.getLocationPath(history.location);
    const to = typeof replace === 'string' ? replace : history.createHref(replace);
    const previousRedirect = internals.lifecycleRedirects.get(history);

    if (previousRedirect
        && previousRedirect.from === to
        && previousRedirect.to === from) {
        const err = new Error(`Stopped route redirect loop from ${from} to ${to}`);
        err.code = 'ROUTE_REDIRECT_LOOP';
        err.from = from;
        err.to = to;

        throw err;
    }

    if (previousRedirect?.stopListening) {
        previousRedirect.stopListening();
    }

    history.replace(replace);

    const redirect = {
        from,
        to: internals.getLocationPath(history.location)
    };

    if (typeof history.listen === 'function') {
        redirect.stopListening = history.listen(() => {

            if (internals.lifecycleRedirects.get(history) === redirect) {
                internals.lifecycleRedirects.delete(history);
            }

            redirect.stopListening();
        });
    }

    internals.lifecycleRedirects.set(history, redirect);

    // Still want to mount if it's a parent route
    return !route.childRoutes && !replace.includes(route.path);
};

internals.getLocationPath = (location = {}) => `${location.pathname || ''}${location.search || ''}${location.hash || ''}`;

internals.resolveRouteDependencies = async ({ route, routerProps }) => {

    const { resolveDependencies } = route;

    if (!resolveDependencies) {
        return null;
    }

    const dependencyContext = internals.getRouteDependencyContext({
        route,
        routerProps
    });

    const dependencyResolvers = internals.getRouteDependencyResolvers({
        dependencyContext,
        resolveDependencies
    });

    for (const dependencyResolver of dependencyResolvers) {
        if (typeof dependencyResolver !== 'function') {
            continue;
        }

        const routePath = `${route.basePath || ''}${route.path || ''}`;
        const resolverName = dependencyResolver.name || 'anonymousDependencyResolver';

        const result = await internals.withTimeout(
            Promise.resolve().then(() => dependencyResolver(dependencyContext)),
            route.dependencyTimeoutMs || DEFAULT_DEPENDENCY_TIMEOUT_MS,
            {
                code: 'ROUTE_DEPENDENCY_TIMEOUT',
                dependencyPropNames: dependencyContext.dependencyPropNames,
                resolverName,
                routePath
            }
        );

        if (result?.replace) {
            return result;
        }
    }

    return null;
};

internals.getRouteDependencyContext = ({ route, routerProps }) => {

    const propTypes = route.dependencyPropTypes || internals.getComponentDependencyPropTypes(route.component);
    const dependencyPropNames = route.dependencyPropNames || internals.getComponentDependencyPropNames(route.component, propTypes);

    return {
        dependencyPropNames,
        propTypes,
        route,
        routerProps
    };
};

internals.getRouteDependencyResolvers = ({ dependencyContext, resolveDependencies }) => {

    if (typeof resolveDependencies === 'function') {
        return [resolveDependencies];
    }

    if (Array.isArray(resolveDependencies)) {
        return resolveDependencies;
    }

    if (!resolveDependencies || typeof resolveDependencies !== 'object') {
        return [];
    }

    const matchedResolvers = dependencyContext.dependencyPropNames
        .map((propName) => resolveDependencies[propName])
        .filter(Boolean);

    const alwaysResolvers = [].concat(resolveDependencies.$always || []);

    return Array.from(new Set(matchedResolvers.concat(alwaysResolvers)));
};

internals.withTimeout = (promise, timeoutMs, timeoutDetails) => {

    return new Promise((resolve, reject) => {

        const timeout = setTimeout(() => {

            const err = new Error(`Timed out resolving dependencies for route ${timeoutDetails.routePath}`);
            err.code = timeoutDetails.code;
            err.routePath = timeoutDetails.routePath;
            err.resolverName = timeoutDetails.resolverName;
            err.dependencyPropNames = timeoutDetails.dependencyPropNames;

            reject(err);
        }, timeoutMs);

        Promise.resolve(promise)
            .then((result) => {

                clearTimeout(timeout);
                resolve(result);
            })
            .catch((err) => {

                clearTimeout(timeout);
                reject(err);
            });
    });
};

internals.getMissingDependencies = ({
    componentName,
    dependencyPropNames,
    propTypes,
    props
}) => {

    return dependencyPropNames.filter((propName) => {

        return internals.hasPropTypeWarnings({
            componentName,
            propName,
            propType: propTypes[propName],
            props
        });
    });
};

internals.getWrappedComponentChain = (Component, maxDepth = 10) => {

    const components = [];
    const seen = new Set();
    let current = Component;

    while (current && !seen.has(current) && components.length < maxDepth) {
        components.push(current);
        seen.add(current);
        current = current.WrappedComponent;
    }

    return components;
};

internals.findFirstComponentValue = (Component, getter) => {

    return internals.getWrappedComponentChain(Component)
        .map(getter)
        .find(Boolean);
};

internals.getComponentDependencyPropNames = (Component, propTypes = internals.getComponentDependencyPropTypes(Component)) => {

    return internals.findFirstComponentValue(Component, (WrappedComponent) => WrappedComponent.dependencyPropNames)
        || internals.getDependencyPropNames(propTypes);
};

internals.getComponentDependencyPropTypes = (Component) => {

    return internals.findFirstComponentValue(Component, (WrappedComponent) => WrappedComponent.dependencyPropTypes)
        || internals.findFirstComponentValue(Component, (WrappedComponent) => WrappedComponent.propTypes)
        || {};
};

internals.getDependencyPropNames = (propTypes) => {

    return Object.keys(propTypes).filter((propName) => {

        const propType = propTypes[propName];

        // Only top-level required props are route-blocking dependencies by default.
        // Nested .isRequired fields are still validated later when the parent prop exists.
        return internals.hasPropTypeWarnings({
            componentName: 'dependency-detection',
            propName,
            propType,
            props: {}
        });
    });
};

internals.hasPropTypeWarnings = ({ componentName, propName, propType, props }) => {

    if (typeof propType !== 'function') {
        return false;
    }

    const capturedWarnings = [];
    const originalError = console.error;

    try {
        if (T.resetWarningCache) {
            T.resetWarningCache();
        }

        console.error = (message, ...args) => {

            capturedWarnings.push(String(message));

            if (!internals.isDependencyPropTypeWarning(message)) {
                originalError(message, ...args);
            }
        };

        T.checkPropTypes(
            { [propName]: propType },
            props,
            'prop',
            componentName
        );
    }
    finally {
        console.error = originalError;
    }

    return capturedWarnings.some(internals.isDependencyPropTypeWarning);
};

internals.isPropTypeWarning = (message) => String(message).includes('Failed prop type');

internals.recordRouteDependencyDebug = (event, details = {}) => {

    if (typeof window === 'undefined') {
        return;
    }

    const current = window.__nearpeerRouteDependencyDebug || {};
    const entry = {
        at: new Date().toISOString(),
        event,
        ...details
    };

    window.__nearpeerRouteDependencyDebug = {
        ...current,
        ...entry,
        recent: [entry].concat(current.recent || []).slice(0, 20)
    };

    window.dispatchEvent(new Event('nearpeer:client-debug:update'));
};

internals.RouteDependencyFallback = function RouteDependencyFallback(props) {

    const {
        componentName,
        missingDependencies,
        location,
        match
    } = props;

    internals.recordRouteDependencyDebug('component-missing-dependencies', {
        componentName,
        missingDependencies,
        pathname: location?.pathname || match?.url || window.location?.pathname
    });

    return <LoadingScreen variant='content' message='Loading...' />;
};

internals.RouteDependencyFallback.propTypes = {
    componentName: T.string,
    missingDependencies: T.array,
    location: T.object,
    match: T.object
};

internals.isDependencyPropTypeWarning = (message) => {

    message = String(message);

    return internals.isPropTypeWarning(message) && (
        message.includes('is marked as required') ||
        message.includes('is waiting for')
    );
};

internals.RouteDependencyError = function RouteDependencyError(props) {

    const {
        componentName,
        missingDependencies,
        routeError
    } = props;

    const routePath = routeError?.routePath || props.location?.pathname || props.match?.url;
    const resolverName = routeError?.resolverName;
    const dependencyPropNames = routeError?.dependencyPropNames || missingDependencies || [];

    console.warn('[router] route dependency fallback rendered', {
        componentName,
        dependencyPropNames,
        resolverName,
        routeError,
        routePath
    });

    return (
        <div style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: '50vh',
            padding: '2rem',
            textAlign: 'center'
        }}>
            <div style={{
                maxWidth: 420
            }}>
                <h2 style={{
                    fontSize: '1.25rem',
                    marginBottom: '0.75rem'
                }}>
                    Still loading this page
                </h2>
                <p style={{
                    lineHeight: 1.4,
                    marginBottom: '1rem'
                }}>
                    We are having trouble loading everything this page needs. This can happen after a refresh or direct link while the app catches up.
                </p>
                <button
                    onClick={reloadPage}
                    style={{
                        border: 0,
                        borderRadius: 4,
                        cursor: 'pointer',
                        fontWeight: 600,
                        padding: '0.75rem 1rem'
                    }}
                    type="button"
                >
                    Try again
                </button>
            </div>
        </div>
    );
};

internals.concatPaths = (base, path) => {

    base = base || '';
    path = path || '';

    base = base.endsWith('/') ? base.slice(0, -1) : base; // /my-path/ -> /my-path
    path = path.startsWith('/') ? path.slice(1) : path;   // /my-path -> my-path

    return `${base}/${path}`;
};


if (process.env.NODE_ENV === 'test') {
    exports._internals = internals;
    exports._RouteDependencyError = internals.RouteDependencyError;
    exports._withPropTypeDependencies = exports.withPropTypeDependencies || withPropTypeDependencies;
}
